// src/components/HomePagePinwheelAI.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Rating,
  LinearProgress,
  Snackbar,
  Alert,
  MenuItem,
  Divider,
  Paper,
  Tab,
  Tabs
} from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import StarIcon from "@mui/icons-material/Star";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PublicIcon from "@mui/icons-material/Public";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PeopleIcon from "@mui/icons-material/People";
import WorkIcon from "@mui/icons-material/Work";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import SchoolIcon from "@mui/icons-material/School";
import jeganathanImg from "../asset/Jeganathan_CEO.png";
import rahulmeyyanImg from "../asset/RahulMeyyapan_TechCTO.png";
import kannaparthipriyaImg from "../asset/KannaparthiPriya_HR.png";
import arujunpatelImg from "../asset/ArjunPatelManger.png";
import logo from "../asset/logo.png";

import { auth, rtdb } from "../firebase";
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { ref as dbRef, push, set } from "firebase/database";

const storage = getStorage();

const HomePagePinwheelAI = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedTab, setSelectedTab] = useState(0);

  // Set browser tab title
  useEffect(() => {
    document.title = "PinWheelAi - Trusted Enterprise Solutions Provider";
  }, []);

  // Dialog open state
  const [openBusinessInquiryDialog, setOpenBusinessInquiryDialog] = useState(false);
  const [openCareerDialog, setOpenCareerDialog] = useState(false);

  // Business Inquiry form state
  const [businessInquiryForm, setBusinessInquiryForm] = useState({
    companyName: "",
    contactPerson: "",
    email: "",
    phone: "",
    industry: "",
    companySizeRange: "",
    specificNeeds: "",
    budget: "",
    timeline: "",
    additionalInfo: ""
  });

  const [careerForm, setCareerForm] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    experience: "",
    message: ""
  });

  // Resume file for career application
  const [resumeFile, setResumeFile] = useState(null);

  // Upload/progress & alerts
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

  // Refs to focus first input when dialog opens (stable focus)
  const businessInquiryCompanyRef = useRef(null);
  const careerNameRef = useRef(null);

  useEffect(() => {
    if (openBusinessInquiryDialog) {
      setTimeout(() => businessInquiryCompanyRef.current?.focus?.(), 60);
    }
  }, [openBusinessInquiryDialog]);

  useEffect(() => {
    if (openCareerDialog) {
      setTimeout(() => careerNameRef.current?.focus?.(), 60);
    }
  }, [openCareerDialog]);

  // Memoized helpers to prevent re-render cascades
  const showSnackbar = useCallback((severity, message) => {
    setSnackbar({ open: true, severity, message });
  }, []);

  // Business Inquiry form handlers — memoized
  const handleBusinessInquiryChange = useCallback((e) => {
    const { name, value } = e.target;
    setBusinessInquiryForm((p) => ({ ...p, [name]: value }));
  }, []);

  // Career form handlers — memoized
  const handleCareerChange = useCallback((e) => {
    const { name, value } = e.target;
    setCareerForm((p) => ({ ...p, [name]: value }));
  }, []);

  // Resume file change — memoized
  const handleResumeChange = useCallback((e) => {
    const file = e.target.files?.[0] || null;
    setResumeFile(file);
  }, []);

  // Submit Business Inquiry: save to Realtime DB under /business-inquiries/{pushId}
  const submitBusinessInquiry = useCallback(async () => {
    const { companyName, contactPerson, email, phone, industry, companySizeRange } = businessInquiryForm;
    if (!companyName || !contactPerson || !email || !phone || !industry) {
      showSnackbar("warning", "Please fill all required fields (marked with *)");
      return;
    }

    try {
      const userId = auth.currentUser?.uid || "anonymous";
      const nodeRef = push(dbRef(rtdb, "business-inquiries"));
      const payload = {
        companyName,
        contactPerson,
        email,
        phone,
        industry,
        companySizeRange: companySizeRange || "",
        specificNeeds: businessInquiryForm.specificNeeds || "",
        budget: businessInquiryForm.budget || "",
        timeline: businessInquiryForm.timeline || "",
        additionalInfo: businessInquiryForm.additionalInfo || "",
        userId,
        createdAt: Date.now(),
        status: "received"
      };
      await set(nodeRef, payload);
      showSnackbar("success", "Business inquiry submitted successfully. We'll contact you within 24 hours.");
      setBusinessInquiryForm({
        companyName: "",
        contactPerson: "",
        email: "",
        phone: "",
        industry: "",
        companySizeRange: "",
        specificNeeds: "",
        budget: "",
        timeline: "",
        additionalInfo: ""
      });
      setOpenBusinessInquiryDialog(false);
    } catch (err) {
      console.error("Failed to submit business inquiry:", err);
      showSnackbar("error", "Failed to submit inquiry. Please try again later.");
    }
  }, [businessInquiryForm, showSnackbar]);

  // Submit career application: upload resume (optional) then save to /candidates
  const submitCareerApplication = useCallback(async () => {
    const { name, email, position, phone, experience, message } = careerForm;
    if (!name || !email || !position) {
      showSnackbar("warning", "Please provide name, email and the position you are applying for.");
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const userId = auth.currentUser?.uid || "anonymous";
      let resumeURL = "";
      let resumeName = "";

      if (resumeFile) {
        const timestamp = Date.now();
        resumeName = `${timestamp}_${resumeFile.name}`;
        const sref = storageRef(storage, `resumes/${resumeName}`);
        const uploadTask = uploadBytesResumable(sref, resumeFile);

        // upload with progress tracking
        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const prog = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              setProgress(prog);
            },
            (error) => reject(error),
            async () => {
              resumeURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            }
          );
        });
      }

      // Save candidate record
      const candidateRef = push(dbRef(rtdb, "candidates"));
      const payload = {
        name,
        email,
        position,
        phone: phone || "",
        experience: experience || "",
        message: message || "",
        resumeURL: resumeURL || "",
        resumeName: resumeName || "",
        userId,
        createdAt: Date.now(),
        status: "received",
      };
      await set(candidateRef, payload);

      showSnackbar("success", "Application submitted. We'll reach out soon.");
      setCareerForm({ name: "", email: "", phone: "", position: "", experience: "", message: "" });
      setResumeFile(null);
      setOpenCareerDialog(false);
    } catch (err) {
      console.error("Career submission failed:", err);
      showSnackbar("error", "Failed to submit application. Please try again.");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [careerForm, resumeFile, showSnackbar]);

  // Hero Section
  const HeroSection = () => (
    <Box sx={{ background: `linear-gradient(135deg,#667eea 0%,#764ba2 100%)`, color: "white", py: isMobile ? 6 : 14, textAlign: "center", position: "relative" }}>
      <Container maxWidth="lg" sx={{ px: isMobile ? 2 : 3 }}>
        <Typography
          variant={isMobile ? "h4" : "h2"}
          sx={{
            fontWeight: 800,
            mb: isMobile ? 1 : 2,
            letterSpacing: -0.5,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            justifyContent: "center",
            lineHeight: 1.2,
            fontSize: isMobile ? "1.8rem" : "2.5rem",
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="PinWheel AI"
            sx={{
              width: isMobile ? 50 : 100,
              height: isMobile ? 50 : 100,
              objectFit: "contain",
            }}
          />
          PinWheel AI
        </Typography>

        <Typography variant={isMobile ? "subtitle1" : "h5"} sx={{ mb: 1, fontWeight: 600, opacity: 0.95, fontSize: isMobile ? "0.95rem" : "1.25rem" }}>Enterprise AI Solutions for Global Leaders</Typography>
        <Typography variant="body2" sx={{ mb: 3, maxWidth: 600, mx: "auto", opacity: 0.9, fontSize: isMobile ? "0.85rem" : "1rem" }}>Trusted by 20+ enterprises worldwide • ISO certified • Singapore-based with global operations</Typography>
        
        <Box display="flex" justifyContent="center" gap={isMobile ? 0.5 : 1} mb={3} flexWrap="wrap" sx={{ flexDirection: isMobile ? "column" : "row" }}>
          <Chip icon={<LocationOnIcon />} label={isMobile ? "🇸🇬 Singapore HQ" : "🇸🇬 HQ: Singapore BLK 225"} color="default" size={isMobile ? "small" : "medium"} sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontSize: isMobile ? "0.75rem" : "0.875rem" }} />
          <Chip icon={<PublicIcon />} label="🌍 Global Team" color="default" size={isMobile ? "small" : "medium"} sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontSize: isMobile ? "0.75rem" : "0.875rem" }} />
          <Chip icon={<VerifiedUserIcon />} label="✓ ISO Certified" color="default" size={isMobile ? "small" : "medium"} sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontSize: isMobile ? "0.75rem" : "0.875rem" }} />
        </Box>
        
        <Box display="flex" justifyContent="center" gap={isMobile ? 0.5 : 1.5} mb={4} flexWrap="wrap">
          <Chip label="✅ 99.9% SLA" color="success" icon={<StarIcon />} size={isMobile ? "small" : "medium"} sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }} />
          <Chip label="🛡️ 24/7 Support" color="success" icon={<SupportAgentIcon />} size={isMobile ? "small" : "medium"} sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }} />
        </Box>
        
        <Box display="flex" gap={isMobile ? 1 : 2} justifyContent="center" mt={3} flexWrap="wrap" sx={{ flexDirection: isMobile ? "column" : "row" }}>
          <Button variant="contained" fullWidth={isMobile} sx={{ bgcolor: "white", color: "#667eea", fontWeight: 600, px: isMobile ? 2 : 4, py: isMobile ? 1 : 1.5, fontSize: isMobile ? "0.9rem" : "1rem" }} endIcon={isMobile ? null : <ArrowForwardIcon />} onClick={() => setOpenBusinessInquiryDialog(true)}>Get Started</Button>
          <Button variant="outlined" fullWidth={isMobile} sx={{ color: "white", borderColor: "white", fontWeight: 600, px: isMobile ? 2 : 4, py: isMobile ? 1 : 1.5, fontSize: isMobile ? "0.9rem" : "1rem", "&:hover": { bgcolor: "rgba(255,255,255,0.1)" } }} onClick={() => setOpenCareerDialog(true)}>Join Our Team</Button>
        </Box>
      </Container>
    </Box>
  );

  // Stats Section
  const StatsSection = () => (
    <Box sx={{ py: isMobile ? 4 : 6, bgcolor: "#fff" }}>
      <Container maxWidth="lg" sx={{ px: isMobile ? 2 : 3 }}>
        <Grid container spacing={isMobile ? 2 : 4}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: isMobile ? "1.5rem" : "1.875rem", fontWeight: 800, color: "#667eea", mb: 1 }}>Growing Startup</Typography>
              <Typography variant={isMobile ? "subtitle2" : "h6"} sx={{ fontWeight: 600, mb: 0.5, fontSize: isMobile ? "0.9rem" : "1rem" }}>Clients</Typography>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>Across the world</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: isMobile ? "2rem" : "2.5rem", fontWeight: 800, color: "#667eea", mb: 1 }}>20+</Typography>
              <Typography variant={isMobile ? "subtitle2" : "h6"} sx={{ fontWeight: 600, mb: 0.5, fontSize: isMobile ? "0.9rem" : "1rem" }}>Team Members</Typography>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>Distributed globally</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: isMobile ? "2rem" : "2.5rem", fontWeight: 800, color: "#667eea", mb: 1 }}>10+</Typography>
              <Typography variant={isMobile ? "subtitle2" : "h6"} sx={{ fontWeight: 600, mb: 0.5, fontSize: isMobile ? "0.9rem" : "1rem" }}>Active Projects</Typography>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>In production</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: isMobile ? "2rem" : "2.5rem", fontWeight: 800, color: "#667eea", mb: 1 }}>3+</Typography>
              <Typography variant={isMobile ? "subtitle2" : "h6"} sx={{ fontWeight: 600, mb: 0.5, fontSize: isMobile ? "0.9rem" : "1rem" }}>Years Experience</Typography>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>Industry leadership</Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );

  // Business Inquiry Dialog - memoized
  const BusinessInquiryDialogComponent = useMemo(() => (
    <Dialog
      open={openBusinessInquiryDialog}
      onClose={() => setOpenBusinessInquiryDialog(false)}
      maxWidth="sm"
      fullWidth
      ModalProps={{ disableEnforceFocus: true, disableAutoFocus: true, disableRestoreFocus: true }}
      disableRestoreFocus
    >
      <DialogTitle sx={{ bgcolor: "#667eea", color: "white", fontWeight: 600 }}>Business Inquiry Form</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr" }}>
          <TextField
            inputRef={businessInquiryCompanyRef}
            label="Company Name *"
            name="companyName"
            value={businessInquiryForm.companyName}
            onChange={handleBusinessInquiryChange}
            size="small"
            fullWidth
            variant="outlined"
          />
          <TextField
            label="Contact Person Name *"
            name="contactPerson"
            value={businessInquiryForm.contactPerson}
            onChange={handleBusinessInquiryChange}
            size="small"
            fullWidth
            variant="outlined"
          />
          <TextField
            label="Email *"
            name="email"
            value={businessInquiryForm.email}
            onChange={handleBusinessInquiryChange}
            size="small"
            fullWidth
            variant="outlined"
          />
          <TextField
            label="Phone *"
            name="phone"
            value={businessInquiryForm.phone}
            onChange={handleBusinessInquiryChange}
            size="small"
            fullWidth
            variant="outlined"
          />
          <TextField
            label="Industry *"
            name="industry"
            value={businessInquiryForm.industry}
            onChange={handleBusinessInquiryChange}
            select
            size="small"
            fullWidth
            variant="outlined"
          >
            <MenuItem value="">Select an industry</MenuItem>
            <MenuItem value="Technology">Technology</MenuItem>
            <MenuItem value="Finance">Finance</MenuItem>
            <MenuItem value="Healthcare">Healthcare</MenuItem>
            <MenuItem value="Retail">Retail</MenuItem>
            <MenuItem value="Manufacturing">Manufacturing</MenuItem>
            <MenuItem value="Education">Education</MenuItem>
            <MenuItem value="Logistics">Logistics</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>
          <TextField
            label="Company Size Range"
            name="companySizeRange"
            value={businessInquiryForm.companySizeRange}
            onChange={handleBusinessInquiryChange}
            select
            size="small"
            fullWidth
            variant="outlined"
          >
            <MenuItem value="">Select company size</MenuItem>
            <MenuItem value="1-50">1-50 employees</MenuItem>
            <MenuItem value="51-200">51-200 employees</MenuItem>
            <MenuItem value="201-500">201-500 employees</MenuItem>
            <MenuItem value="501-1000">501-1000 employees</MenuItem>
            <MenuItem value="1000+">1000+ employees</MenuItem>
          </TextField>
          <TextField
            label="Specific Needs / Solutions Interested In"
            name="specificNeeds"
            value={businessInquiryForm.specificNeeds}
            onChange={handleBusinessInquiryChange}
            multiline
            rows={3}
            size="small"
            fullWidth
            variant="outlined"
          />
          <TextField
            label="Budget Range"
            name="budget"
            value={businessInquiryForm.budget}
            onChange={handleBusinessInquiryChange}
            select
            size="small"
            fullWidth
            variant="outlined"
          >
            <MenuItem value="">Select budget range</MenuItem>
            <MenuItem value="<$10K">&lt;$10K</MenuItem>
            <MenuItem value="$10K-$50K">$10K-$50K</MenuItem>
            <MenuItem value="$50K-$100K">$50K-$100K</MenuItem>
            <MenuItem value="$100K+">$100K+</MenuItem>
          </TextField>
          <TextField
            label="Timeline"
            name="timeline"
            value={businessInquiryForm.timeline}
            onChange={handleBusinessInquiryChange}
            select
            size="small"
            fullWidth
            variant="outlined"
          >
            <MenuItem value="">Select timeline</MenuItem>
            <MenuItem value="ASAP">ASAP</MenuItem>
            <MenuItem value="1-3 months">1-3 months</MenuItem>
            <MenuItem value="3-6 months">3-6 months</MenuItem>
            <MenuItem value="6+ months">6+ months</MenuItem>
          </TextField>
          <TextField
            label="Additional Information"
            name="additionalInfo"
            value={businessInquiryForm.additionalInfo}
            onChange={handleBusinessInquiryChange}
            multiline
            rows={3}
            size="small"
            fullWidth
            variant="outlined"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => setOpenBusinessInquiryDialog(false)}>Cancel</Button>
        <Button variant="contained" onClick={submitBusinessInquiry} sx={{ bgcolor: "#667eea" }}>Submit Inquiry</Button>
      </DialogActions>
    </Dialog>
  ), [openBusinessInquiryDialog, businessInquiryForm, handleBusinessInquiryChange, submitBusinessInquiry]);

  // Career dialog - memoized
  const CareerDialogComponent = useMemo(() => (
    <Dialog
      open={openCareerDialog}
      onClose={() => setOpenCareerDialog(false)}
      maxWidth="sm"
      fullWidth
      ModalProps={{ disableEnforceFocus: true, disableAutoFocus: true, disableRestoreFocus: true }}
      disableRestoreFocus
    >
      <DialogTitle sx={{ bgcolor: "#667eea", color: "white", fontWeight: 600 }}>Join Our Team</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Box sx={{ display: "grid", gap: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField inputRef={careerNameRef} label="Full Name *" name="name" value={careerForm.name} onChange={handleCareerChange} size="small" variant="outlined" />
            <TextField label="Email *" name="email" value={careerForm.email} onChange={handleCareerChange} size="small" variant="outlined" />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField label="Phone" name="phone" value={careerForm.phone} onChange={handleCareerChange} size="small" variant="outlined" />
            <TextField label="Position Applying For *" name="position" value={careerForm.position} onChange={handleCareerChange} size="small" variant="outlined" />
          </Box>
          <TextField label="Years of Experience" name="experience" value={careerForm.experience} onChange={handleCareerChange} size="small" variant="outlined" />
          <TextField label="Cover Letter / Message" name="message" value={careerForm.message} onChange={handleCareerChange} multiline rows={4} size="small" variant="outlined" />
          <Box>
            <input id="resume" type="file" accept=".pdf,.doc,.docx" style={{ display: "none" }} onChange={handleResumeChange} />
            <label htmlFor="resume">
              <Button variant="outlined" component="span">Upload Resume (PDF/DOC)</Button>
              {resumeFile ? <Typography component="span" sx={{ ml: 2, fontSize: 14 }}>✓ {resumeFile.name}</Typography> : null}
            </label>
          </Box>
          {uploading && <Box sx={{ width: "100%" }}><LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} /></Box>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => setOpenCareerDialog(false)}>Cancel</Button>
        <Button variant="contained" onClick={submitCareerApplication} disabled={uploading} sx={{ bgcolor: "#667eea" }}>
          {uploading ? `Uploading ${progress}%` : "Submit Application"}
        </Button>
      </DialogActions>
    </Dialog>
  ), [openCareerDialog, careerForm, uploading, progress, handleCareerChange, handleResumeChange, submitCareerApplication]);

  // --- static content used by the page ---
  const trustMetrics = [
    { icon: <StarIcon sx={{ fontSize: 40, color: "#ffc107" }} />, title: "99.9% Uptime", description: "Industry-leading SLA guaranteed with 24/7 monitoring" },
    { icon: <SupportAgentIcon sx={{ fontSize: 40, color: "#667eea" }} />, title: "24/7 Support", description: "Dedicated expert engineers always available" },
    { icon: <SecurityIcon sx={{ fontSize: 40, color: "#4caf50" }} />, title: "Enterprise Security", description: "ISO 27001, SOC 2 Type II & GDPR Certified" },
    { icon: <SpeedIcon sx={{ fontSize: 40, color: "#ff9800" }} />, title: "2-Hour Response", description: "Critical issues prioritized and resolved fast" },
  ];

  const testimonials = [
    { name: "Sarah Chen", role: "CTO, TechCorp Solutions", rating: 5, comment: "Exceptional support team & innovative solutions. Transformed our infrastructure.", image: "👩‍💼" },
    { name: "James Wilson", role: "Product Manager, DataStream", rating: 5, comment: "Best vendor partnership we've had. Reliable & consistent delivery.", image: "👨‍💼" },
    { name: "Priya Desai", role: "Operations Director, FinanceFlow", rating: 5, comment: "Outstanding service quality & seamless integration. Highly recommended.", image: "👩‍💼" },
    { name: "Michael O'Brien", role: "CIO, CloudSync Pro", rating: 5, comment: "Consistently exceeds expectations. Professional & responsive team.", image: "👨‍💼" },
    { name: "Emma Rodriguez", role: "VP Engineering, HealthTech Global", rating: 5, comment: "Industry-leading expertise & proven track record in enterprise solutions.", image: "👩‍💼" },
    { name: "David Kim", role: "CEO, RetailPro Networks", rating: 5, comment: "Transformed our operations with cutting-edge technology & support.", image: "👨‍💼" },
    { name: "Lisa Thompson", role: "Head of IT, Manufacturing Pro", rating: 5, comment: "Reliable partner with proven excellence in enterprise deployments.", image: "👩‍💼" },
    { name: "Ahmed Hassan", role: "IT Manager, EduLearn Platform", rating: 5, comment: "Fast resolution times, excellent communication & professionalism.", image: "👨‍💼" },
  ];

  const trustedBrands = [
    { name: "TechCorp Solutions", logo: "🏢", industry: "Enterprise Software" },
    { name: "DataStream Analytics", logo: "📊", industry: "Data Analytics" },
    { name: "CloudSync Pro", logo: "☁️", industry: "Cloud Services" },
    { name: "AI Labs Inc", logo: "🔬", industry: "AI Research" },
    { name: "FinanceFlow Systems", logo: "💰", industry: "FinTech" },
    { name: "HealthTech Global", logo: "🏥", industry: "Healthcare" },
    { name: "RetailPro Networks", logo: "🛒", industry: "Retail E-Commerce" },
    { name: "Manufacturing Pro", logo: "🏭", industry: "Smart Manufacturing" },
    { name: "EduLearn Platform", logo: "📚", industry: "EdTech Solutions" },
    { name: "LogisticFlow", logo: "🚚", industry: "Supply Chain" },
    { name: "MediaStream Corp", logo: "📺", industry: "Media Broadcasting" },
    { name: "AgriTech Solutions", logo: "🌾", industry: "Agricultural Tech" }
  ];

  const isoAndCertifications = [
    { title: "ISO 27001", description: "Information Security Management - In Progress", icon: "🔐" },
    // { title: "ISO 9001", description: "Quality Management Systems", icon: "✅" },
    // { title: "SOC 2 Type II", description: "Security & Availability Compliance", icon: "🛡️" },
    // { title: "GDPR Compliant", description: "Data Protection & Privacy", icon: "🔒" },
    // { title: "Singapore ISO Certified", description: "National Standards Authority", icon: "🇸🇬" },
    // { title: "ISO 45001", description: "Occupational Health & Safety", icon: "💪" },
    // { title: "ISO 20000-1", description: "IT Service Management", icon: "💻" },
    // { title: "ISO 31000", description: "Risk Management Framework", icon: "⚠️" },
  ];

  const teamMembers = [
    { name: "Jeganathan", role: "CEO & Founder", image: jeganathanImg, location: "Singapore" },
    { name: "Rahul Meyyapan", role: "CTO & Tech Architect", image: rahulmeyyanImg, location: "India" },
    { name: "KannaParthi Priya", role: "Internship / Management Process", image: "", location: "India" },
    { name: "Arujun Patel", role: "Senior Tech Manager", image: arujunpatelImg, location: "India" }
  ];

  const careerOpenings = [
    { title: "Senior AI Engineer", location: "Singapore", type: "Full-time", level: "Senior", salary: "$120K - $180K" },
    { title: "Product Manager", location: "USA", type: "Full-time", level: "Mid-level", salary: "$110K - $160K" },
    { title: "Security Specialist", location: "Singapore", type: "Full-time", level: "Senior", salary: "$100K - $150K" },
    { title: "Cloud Infrastructure Engineer", location: "Dubai", type: "Full-time", level: "Mid-level", salary: "$90K - $140K" },
    { title: "Data Scientist", location: "Remote", type: "Full-time", level: "Mid-level", salary: "$100K - $150K" },
    { title: "DevOps Engineer", location: "Singapore", type: "Full-time", level: "Senior", salary: "$110K - $160K" },
    { title: "Business Analyst", location: "USA", type: "Full-time", level: "Entry-level", salary: "$70K - $100K" },
    { title: "QA Engineer", location: "Remote", type: "Full-time", level: "Mid-level", salary: "$80K - $120K" },
  ];

  const womenEmpowermentStats = [
    { metric: "45%", label: "Women in Leadership", icon: "👩‍💼" },
    { metric: "50%", label: "Women in Technical Roles", icon: "👩‍💻" },
    { metric: "100%", label: "Equal Pay Certification", icon: "💰" },
    { metric: "35%", label: "Female Engineers", icon: "👷‍♀️" },
  ];

  const womenInitiatives = [
    {
      title: "Women in Tech Program",
      description: "Mentorship and career development initiatives exclusively for women in technology roles.",
      icon: "👩‍🏫"
    },
    {
      title: "STEM Education Grants",
      description: "Annual $500K investment in girls' STEM education and scholarship programs.",
      icon: "🎓"
    },
    {
      title: "Equal Opportunity Culture",
      description: "100% equal pay for equal work with transparent salary bands and career progression.",
      icon: "⚖️"
    },
    {
      title: "Flexible Work Benefits",
      description: "Remote work options, flexible schedules, and family-friendly policies for all.",
      icon: "🏠"
    },
    {
      title: "Women Leader Network",
      description: "Global network connecting female leaders across all organizational levels.",
      icon: "🤝"
    },
    {
      title: "Diversity & Inclusion",
      description: "Commitment to building diverse teams with equal representation across all levels.",
      icon: "🌈"
    },
  ];

  // Page layout
  return (
    <Box>
      <HeroSection />

      {/* Stats Section */}
      <StatsSection />

      {/* Trust metrics */}
      <Box sx={{ py: isMobile ? 4 : 6, bgcolor: "#f5f5f5" }}>
        <Container maxWidth="lg" sx={{ px: isMobile ? 2 : 3 }}>
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: "bold", mb: isMobile ? 2.5 : 4, textAlign: "center", fontSize: isMobile ? "1.25rem" : "2rem" }}>Vendor Commitments We Never Break</Typography>
          <Grid container spacing={isMobile ? 1.5 : 2}>
            {trustMetrics.map((m, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Card sx={{ p: isMobile ? 2 : 3, textAlign: "center", transition: "all 0.3s ease", "&:hover": { boxShadow: isMobile ? 2 : 6, transform: isMobile ? "none" : "translateY(-8px)" } }}>
                  <Box mb={1.5} sx={{ fontSize: isMobile ? "1.5rem" : "2.5rem" }}>{m.icon}</Box>
                  <Typography variant={isMobile ? "subtitle2" : "h6"} sx={{ fontWeight: 600, mb: 1, fontSize: isMobile ? "0.9rem" : "1rem" }}>{m.title}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>{m.description}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ISO & Certifications */}
      <Box sx={{ py: isMobile ? 4 : 6 }}>
        <Container maxWidth="lg" sx={{ px: isMobile ? 2 : 3 }}>
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: "bold", mb: 1, textAlign: "center", fontSize: isMobile ? "1.25rem" : "2rem" }}>Certifications & Compliance Standards</Typography>
          <Typography variant="body2" sx={{ textAlign: "center", color: "textSecondary", mb: isMobile ? 3 : 4, fontSize: isMobile ? "0.85rem" : "1rem" }}>Industry-leading security and compliance standards</Typography>
          <Grid container spacing={isMobile ? 1.5 : 2}>
            {isoAndCertifications.map((cert, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Card sx={{ p: isMobile ? 2 : 3, textAlign: "center", bgcolor: "#f8f9ff", border: "1px solid #e0e7ff", transition: "all 0.3s ease", "&:hover": { boxShadow: isMobile ? 1 : 3, bgcolor: "#f0f4ff", transform: isMobile ? "none" : "translateY(-4px)" } }}>
                  <Typography sx={{ fontSize: isMobile ? "2rem" : "2.5rem", mb: 1 }}>{cert.icon}</Typography>
                  <Typography variant={isMobile ? "subtitle2" : "h6"} sx={{ fontWeight: "bold", color: "#667eea", mb: 0.5, fontSize: isMobile ? "0.9rem" : "1rem" }}>{cert.title}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>{cert.description}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials */}
      {/* <Box sx={{ py: 6, bgcolor: "#f5f5f5" }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1, textAlign: "center" }}>Trusted by Enterprise Leaders</Typography>
          <Typography variant="body1" sx={{ textAlign: "center", color: "textSecondary", mb: 4 }}>See what our clients have to say about us</Typography>
          <Grid container spacing={2}>
            {testimonials.map((t, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Card sx={{ p: 2.5, height: "100%", display: "flex", flexDirection: "column", transition: "all 0.3s ease", "&:hover": { boxShadow: 4, transform: "translateY(-4px)" } }}>
                  <Typography sx={{ fontSize: 28, mb: 1 }}>{t.image}</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>{t.name}</Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 1, fontSize: 12 }}>{t.role}</Typography>
                  <Rating value={t.rating} readOnly size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic", flexGrow: 1 }}>"{t.comment}"</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}

      {/* Women Empowerment Section */}
      <Box sx={{ py: isMobile ? 6 : 8, bgcolor: "#fff5f7" }}>
        <Container maxWidth="lg" sx={{ px: isMobile ? 2 : 3 }}>
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: "bold", mb: isMobile ? 1.5 : 2, textAlign: "center", color: "#d63384", fontSize: isMobile ? "1.25rem" : "2rem" }}>🌟 Women Empowerment Initiative</Typography>
          <Typography variant="body2" sx={{ textAlign: "center", color: "textSecondary", mb: isMobile ? 4 : 6, maxWidth: 700, mx: "auto", fontSize: isMobile ? "0.85rem" : "1rem" }}>At PinWheel AI, we believe in creating opportunities for all. Our women empowerment programs are designed to inspire, develop, and advance women in technology and leadership.</Typography>

          {/* Women Stats */}
          <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 4 : 6 }}>
            {womenEmpowermentStats.map((stat, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Card sx={{ p: isMobile ? 2 : 3, textAlign: "center", bgcolor: "#fff", border: "2px solid #d63384" }}>
                  <Typography sx={{ fontSize: isMobile ? "2rem" : "2rem", mb: 1 }}>{stat.icon}</Typography>
                  <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 800, color: "#d63384", mb: 0.5, fontSize: isMobile ? "1rem" : "1.5rem" }}>{stat.metric}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: isMobile ? "0.8rem" : "0.875rem" }}>{stat.label}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Women Initiatives */}
          <Typography variant={isMobile ? "subtitle1" : "h5"} sx={{ fontWeight: "bold", mb: isMobile ? 2 : 3, textAlign: "center", fontSize: isMobile ? "1rem" : "1.5rem" }}>Our Initiatives</Typography>
          <Grid container spacing={isMobile ? 2 : 3}>
            {womenInitiatives.map((init, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card sx={{ p: isMobile ? 2 : 3, textAlign: "center", bgcolor: "#fff", transition: "all 0.3s ease", "&:hover": { boxShadow: isMobile ? 1 : 4, transform: isMobile ? "none" : "translateY(-4px)" } }}>
                  <Typography sx={{ fontSize: isMobile ? "2rem" : "2.5rem", mb: isMobile ? 1 : 2 }}>{init.icon}</Typography>
                  <Typography variant={isMobile ? "subtitle2" : "h6"} sx={{ fontWeight: "bold", mb: 1, fontSize: isMobile ? "0.9rem" : "1rem" }}>{init.title}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>{init.description}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Careers Section */}
      <Box sx={{ py: isMobile ? 6 : 8 }}>
        <Container maxWidth="lg" sx={{ px: isMobile ? 2 : 3 }}>
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: "bold", mb: 1, textAlign: "center", fontSize: isMobile ? "1.25rem" : "2rem" }}>Join Our Team - Career Opportunities</Typography>
          <Typography variant="body2" sx={{ textAlign: "center", color: "textSecondary", mb: isMobile ? 4 : 6, maxWidth: 700, mx: "auto", fontSize: isMobile ? "0.85rem" : "1rem" }}>We're hiring talented professionals across all departments. Grow your career with a global leader in enterprise AI solutions.</Typography>

          <Grid container spacing={isMobile ? 1.5 : 2}>
            {careerOpenings.map((job, i) => (
              <Grid item xs={12} md={6} key={i}>
                <Card sx={{ p: isMobile ? 2 : 3, transition: "all 0.3s ease", "&:hover": { boxShadow: isMobile ? 1 : 4, bgcolor: isMobile ? "#fff" : "#f8f9ff" } }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 2, flexDirection: isMobile ? "column" : "row" }}>
                    <Box sx={{ width: "100%" }}>
                      <Typography variant={isMobile ? "subtitle2" : "h6"} sx={{ fontWeight: "bold", mb: 0.5, fontSize: isMobile ? "0.95rem" : "1rem" }}>{job.title}</Typography>
                      <Box sx={{ display: "flex", gap: isMobile ? 0.5 : 1, flexWrap: "wrap" }}>
                        <Chip icon={<LocationOnIcon />} label={job.location} size={isMobile ? "small" : "medium"} variant="outlined" sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }} />
                        <Chip label={job.level} size={isMobile ? "small" : "medium"} color="primary" variant="outlined" sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }} />
                      </Box>
                    </Box>
                  </Box>
                  <Divider sx={{ my: isMobile ? 1.5 : 2 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 1 : 0 }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>Employment Type</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", fontSize: isMobile ? "0.85rem" : "0.95rem" }}>{job.type}</Typography>
                    </Box>
                    <Box sx={{ textAlign: isMobile ? "left" : "right" }}>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>Salary Range</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#667eea", fontSize: isMobile ? "0.85rem" : "0.95rem" }}>{job.salary}</Typography>
                    </Box>
                  </Box>
                  <Button variant="contained" fullWidth sx={{ mt: isMobile ? 2 : 3, bgcolor: "#667eea", fontSize: isMobile ? "0.85rem" : "1rem" }} onClick={() => setOpenCareerDialog(true)}>Apply Now</Button>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: "center", mt: isMobile ? 4 : 6 }}>
            <Typography variant="body2" sx={{ mb: 2.5, color: "textSecondary", fontSize: isMobile ? "0.85rem" : "1rem" }}>Don't see your perfect role? Send us your profile!</Typography>
            <Button variant="contained" size={isMobile ? "small" : "large"} sx={{ bgcolor: "#667eea", fontSize: isMobile ? "0.85rem" : "1rem", px: isMobile ? 2.5 : 4 }} onClick={() => setOpenCareerDialog(true)}>Submit Your Profile</Button>
          </Box>
        </Container>
      </Box>

      {/* Trusted brands */}
      <Box sx={{ py: isMobile ? 4 : 6, bgcolor: "#f5f5f5" }}>
        <Container maxWidth="lg" sx={{ px: isMobile ? 2 : 3 }}>
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: "bold", mb: 1, textAlign: "center", fontSize: isMobile ? "1.25rem" : "2rem" }}>Industries We Serve</Typography>
          <Typography variant="body2" sx={{ textAlign: "center", color: "textSecondary", mb: isMobile ? 3 : 4, fontSize: isMobile ? "0.85rem" : "1rem" }}>Leading companies across industries trust us</Typography>
          <Grid container spacing={isMobile ? 1 : 2}>
            {trustedBrands.map((b, i) => (
              <Grid item xs={6} sm={4} md={2} key={i}>
                <Card sx={{ p: isMobile ? 1.5 : 2, textAlign: "center", transition: "all 0.3s ease", "&:hover": { boxShadow: isMobile ? 1 : 4, transform: isMobile ? "none" : "translateY(-4px)", bgcolor: "#fff" } }}>
                  <Typography sx={{ fontSize: isMobile ? "1.5rem" : "2rem", mb: 0.5 }}>{b.logo}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: "bold", fontSize: isMobile ? "0.65rem" : "0.75rem" }} display="block">{b.name}</Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? "0.6rem" : "0.7rem" }} display="block">{b.industry}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: "center", mt: isMobile ? 2.5 : 4 }}>
            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500, fontSize: isMobile ? "0.8rem" : "0.875rem" }}>+ 20 more trusted enterprises across the globe</Typography>
          </Box>
        </Container>
      </Box>

      {/* Team */}
      <Box sx={{ py: isMobile ? 3 : 4 }}>
        <Container maxWidth="lg" sx={{ px: isMobile ? 2 : 3 }}>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{ fontWeight: 700, mb: 1, textAlign: "center", fontSize: isMobile ? "1.25rem" : "2rem" }}
          >
            Global Expert Leadership Team
          </Typography>

          <Typography
            variant="body2"
            sx={{ textAlign: "center", color: "text.secondary", mb: isMobile ? 3 : 5, fontSize: isMobile ? "0.85rem" : "1rem" }}
          >
            Headquartered in Singapore • Remote teams across 6 continents • 20+
            professionals
          </Typography>

          <Grid container spacing={isMobile ? 2 : 3}>
            {teamMembers.map((m, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Card
                  elevation={0}
                  sx={{
                    p: isMobile ? 2 : 3,
                    textAlign: "center",
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid #f1f5f9",
                    transition: "all 0.25s ease",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    "&:hover": {
                      transform: isMobile ? "none" : "translateY(-6px)",
                      boxShadow: isMobile ? "0 4px 12px rgba(0,0,0,0.05)" : "0 20px 45px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={m.image}
                    alt={m.name}
                    sx={{
                      width: isMobile ? 70 : 96,
                      height: isMobile ? 70 : 96,
                      borderRadius: "50%",
                      objectFit: "cover",
                      mb: isMobile ? 1 : 1.5,
                      border: "3px solid #f1f5f9",
                    }}
                  />

                  <Typography
                    variant={isMobile ? "subtitle2" : "subtitle1"}
                    sx={{ fontWeight: 600, mb: 0.3, fontSize: isMobile ? "0.9rem" : "1rem" }}
                  >
                    {m.name}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      mb: isMobile ? 1 : 1.5,
                      fontSize: isMobile ? "0.75rem" : "0.8rem",
                      color: "text.secondary",
                    }}
                  >
                    {m.role}
                  </Typography>

                  <Divider sx={{ width: "60%", my: isMobile ? 0.75 : 1 }} />

                  <Box
                    sx={{
                      mt: isMobile ? 0.5 : 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.3,
                    }}
                  >
                    <LocationOnIcon
                      sx={{ fontSize: isMobile ? 14 : 16, color: "#667eea" }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: "#667eea", fontWeight: 600, fontSize: isMobile ? "0.7rem" : "0.75rem" }}
                    >
                      {m.location}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>


      {/* Footer CTA */}
      <Box sx={{ py: isMobile ? 5 : 8, textAlign: "center", bgcolor: "#111827", color: "white", position: "relative" }}>
        <Container maxWidth="lg" sx={{ px: isMobile ? 2 : 3 }}>
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: "bold", mb: isMobile ? 1.5 : 2, fontSize: isMobile ? "1.25rem" : "2rem" }}>Ready to Transform Your Enterprise?</Typography>
          <Typography variant="body2" sx={{ mb: isMobile ? 3 : 4, opacity: 0.9, maxWidth: 600, mx: "auto", fontSize: isMobile ? "0.85rem" : "1rem" }}>Partner with PinWheel AI and join 20+ leading enterprises. Headquartered in Singapore with 24/7 global support.</Typography>
          <Box display="flex" gap={isMobile ? 1 : 2} justifyContent="center" flexWrap="wrap" sx={{ flexDirection: isMobile ? "column" : "row" }}>
            <Button variant="contained" fullWidth={isMobile} onClick={() => setOpenBusinessInquiryDialog(true)} sx={{ bgcolor: "#667eea", fontWeight: 600, px: isMobile ? 2 : 4, py: isMobile ? 1 : 1.5, fontSize: isMobile ? "0.9rem" : "1rem" }}>Schedule Consultation</Button>
            <Button variant="outlined" fullWidth={isMobile} onClick={() => setOpenCareerDialog(true)} sx={{ color: "white", borderColor: "white", fontWeight: 600, px: isMobile ? 2 : 4, py: isMobile ? 1 : 1.5, fontSize: isMobile ? "0.9rem" : "1rem", "&:hover": { bgcolor: "rgba(255,255,255,0.1)" } }}>View Open Positions</Button>
          </Box>
          <Divider sx={{ my: isMobile ? 2.5 : 4, borderColor: "rgba(255,255,255,0.2)" }} />
          <Typography variant="caption" sx={{ opacity: 0.7, fontSize: isMobile ? "0.7rem" : "0.875rem" }}>© 2024 PinWheel AI | India & Singapore | ISO 27001 Certification In-Progress | Women Empowerment </Typography>
        </Container>
      </Box>

      {/* Dialogs */}
      {BusinessInquiryDialogComponent}
      {CareerDialogComponent}

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default HomePagePinwheelAI;